/**
 * VIB-723: Kanonisk liste over alle oppgaver på et prosjekt.
 *
 * Brukes av både Timetildeling-tab (gruppér på phase) og Godkjenning-tab
 * (flat liste med status-filter). Ved å dele helper er det enforced i kode
 * at de viser samme sett — ikke ved konvensjon.
 *
 * Inkluderer:
 * - Alle assignments på task-linjer (én ProjectTask per assignment)
 * - Task-linjer uten assignments (én rad med assignment=null, status="unassigned")
 *
 * Filtrerer IKKE bort linjer med ukjent phase — caller bestemmer.
 */
import { isTaskLine } from "@/lib/billing-calculations";
import type { BudgetLineAssignmentRow, BudgetLineRow } from "@/hooks/use-project-detail";

export type ProjectTaskStatus =
  | "unassigned"
  | "not_started"
  | "in_progress"
  | "completed"
  | "approved"
  | "rejected";

export interface ProjectTask {
  budgetLineId: string;
  description: string;
  phase: string | null;
  assignment: BudgetLineAssignmentRow | null;
  status: ProjectTaskStatus;
  loggedHours: number;
}

export function getProjectTasks(
  budgetLines: BudgetLineRow[],
  loggedHoursByAssignment?: Map<string, number>,
  loggedHoursByBudgetLine?: Map<string, number>,
): ProjectTask[] {
  const tasks: ProjectTask[] = [];
  for (const bl of budgetLines.filter(isTaskLine)) {
    const fallback = bl.description ?? "(uten beskrivelse)";
    if ((bl.assignments ?? []).length === 0) {
      tasks.push({
        budgetLineId: bl.id,
        description: fallback,
        phase: bl.phase,
        assignment: null,
        status: "unassigned",
        loggedHours: 0,
      });
    } else {
      for (const a of bl.assignments) {
        const fromAssignment = loggedHoursByAssignment?.get(a.id);
        const fromBL =
          fromAssignment === undefined && loggedHoursByBudgetLine
            ? loggedHoursByBudgetLine.get(bl.id) ?? 0
            : 0;
        tasks.push({
          budgetLineId: bl.id,
          // VIB-671: per-assignment label tar prioritet over budget-line description
          description: a.label ?? fallback,
          phase: bl.phase,
          assignment: a,
          status: (a.task_status as ProjectTaskStatus) ?? "not_started",
          loggedHours: Math.round((fromAssignment ?? fromBL) * 10) / 10,
        });
      }
    }
  }
  return tasks;
}

export function filterByStatus(
  tasks: ProjectTask[],
  status: ProjectTaskStatus | "all",
): ProjectTask[] {
  if (status === "all") return tasks;
  return tasks.filter((t) => t.status === status);
}

/**
 * Gruppér på phase. Tasks uten phase eller med ukjent phase havner i "annet".
 * `knownPhases` skal være listen som UI-en faktisk har labels for (typisk
 * `Object.keys(PHASE_LABELS)` fra offer-types).
 */
export function groupByPhase(
  tasks: ProjectTask[],
  knownPhases: readonly string[],
): Map<string, ProjectTask[]> {
  const groups = new Map<string, ProjectTask[]>();
  for (const t of tasks) {
    const key = t.phase && knownPhases.includes(t.phase) ? t.phase : "annet";
    const arr = groups.get(key) ?? [];
    arr.push(t);
    groups.set(key, arr);
  }
  return groups;
}
